/* eslint-disable react-refresh/only-export-components */
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, ArchiveRestore, FileOutput, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { CapaAction, CapaPlan } from '@/api/capa/types';
import { AuditCapaActionsTable } from '@/app/(client)/audits/-components/digitized/AuditCapaActionsTable';
import { AuditCapaPlansTable } from '@/app/(client)/audits/-components/digitized/AuditCapaPlansTable';
import {
  auditCapaActionsKeys,
  useAuditCapaActions,
} from '@/app/(client)/audits/-hooks/useAuditCapaActions';
import { useAuditCapaCellCommit } from '@/app/(client)/audits/-hooks/useAuditCapaCellCommit';
import {
  auditCapaPlansKeys,
  useAuditCapaPlans,
} from '@/app/(client)/audits/-hooks/useAuditCapaPlans';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { ExportFindingsDialog } from '@/app/(client)/capa/-components/ExportFindingsDialog';
import {
  canEditActionField,
  type EditableActionField,
  getActionRoleForUser,
} from '@/app/(client)/capa/-utils/actionPermissions';
import { CreatePlanDialog } from '@/components/shared/CreatePlanDialog';
import type { BulkActionDef } from '@/components/shared/DataTable/BulkActions';
import type { ToolbarActionDef } from '@/components/shared/DataTable/types';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useArchiveActionsMutation } from '@/hooks/useArchiveActionsMutation';
import { useArchivePlansMutation } from '@/hooks/useArchivePlansMutation';
import { useCreateEntityTagMutation } from '@/hooks/useCreateEntityTagMutation';
import { useCreatePlanDialog } from '@/hooks/useCreatePlanDialog';
import { useEntityTagsQuery } from '@/hooks/useEntityTagsQuery';
import { useImpactAreasQuery } from '@/hooks/useImpactAreasQuery';
import { useLinkEntityTagsMutation } from '@/hooks/useLinkEntityTagsMutation';
import { useTranslator } from '@/hooks/useTranslator';
import { useUnarchiveActionsMutation } from '@/hooks/useUnarchiveActionsMutation';
import { useUnarchivePlansMutation } from '@/hooks/useUnarchivePlansMutation';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/capa/',
)({
  component: AuditCapaPage,
});

function AuditCapaPage() {
  const { auditId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const queryClient = useQueryClient();
  const { data: audit } = useAuditDetail(auditId);
  const myUserId = useSessionStore(s => s.user?.id ?? '');
  const myCompanyId = useSessionStore(s => s.user?.companyId ?? '');

  const [view, setView] = useState<'plans' | 'actions'>('plans');
  const [isArchived, setIsArchived] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [actionSelection, setActionSelection] = useState<
    Record<string, boolean>
  >({});
  const [planSelection, setPlanSelection] = useState<Record<string, boolean>>(
    {},
  );

  const { data: plansData, isLoading: plansLoading } = useAuditCapaPlans(
    auditId,
    isArchived,
  );
  const { data: actionsData, isLoading: actionsLoading } = useAuditCapaActions(
    auditId,
    isArchived,
  );
  const { data: impactAreas } = useImpactAreasQuery();
  const { data: tagsData } = useEntityTagsQuery();
  const tags = tagsData?.data ?? [];

  const createTagMutation = useCreateEntityTagMutation();
  const linkTagsMutation = useLinkEntityTagsMutation();
  const archiveActionsMutation = useArchiveActionsMutation();
  const unarchiveActionsMutation = useUnarchiveActionsMutation();
  const archivePlansMutation = useArchivePlansMutation();
  const unarchivePlansMutation = useUnarchivePlansMutation();

  const createPlanController = useCreatePlanDialog(setCreatePlanOpen, {
    id: auditId,
    companyId: audit?.companyId ?? '',
    name: audit?.name,
    companyName: audit?.companyName ?? undefined,
  });

  const actionSelectedIds = Object.entries(actionSelection)
    .filter(([, v]) => v)
    .map(([id]) => id);
  const planSelectedIds = Object.entries(planSelection)
    .filter(([, v]) => v)
    .map(([id]) => id);

  // Inline-edit permission gates. Actions reuse the shared per-role util;
  // plans have no dedicated util, so gate on company ownership. Archived
  // view is always read-only.
  const canEditAction = (field: EditableActionField, row: CapaAction) =>
    canEditActionField(
      field,
      getActionRoleForUser(row, { id: myUserId, companyId: myCompanyId }),
      isArchived,
    );
  const canEditPlan = (row: CapaPlan) =>
    !isArchived && !!myCompanyId && row.companyId === myCompanyId;

  const onCreateTag = async (
    title: string,
    colorCode: string,
    description: string,
  ) => {
    const res = await createTagMutation.mutateAsync({
      title,
      colorCode,
      description,
    });
    return { value: res.data.id, label: res.data.title };
  };

  const { handleActionCellCommit, handlePlanCellCommit } =
    useAuditCapaCellCommit({ auditId, linkTagsMutation, tags });

  const handleArchiveActions = async () => {
    try {
      const mutation = isArchived
        ? unarchiveActionsMutation
        : archiveActionsMutation;
      await mutation.mutateAsync({ actionIds: actionSelectedIds });
      void queryClient.invalidateQueries({
        queryKey: auditCapaActionsKeys.base(auditId),
      });
      setActionSelection({});
    } catch (err) {
      toast.error(t('capa.updateError'), {
        description: (err as Error).message,
      });
    }
  };

  const handleArchivePlans = async () => {
    try {
      if (isArchived) {
        await unarchivePlansMutation.mutateAsync({
          actionIds: planSelectedIds,
        });
      } else {
        await archivePlansMutation.mutateAsync({
          actionIds: planSelectedIds,
          includeLinkedActions: false,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: auditCapaPlansKeys.base(auditId),
      });
      setPlanSelection({});
    } catch (err) {
      toast.error(t('capa.updateError'), {
        description: (err as Error).message,
      });
    }
  };

  const actionBulkActions: BulkActionDef[] = [
    isArchived
      ? {
          id: 'unarchive',
          type: 'button',
          label: t('capa.unarchive'),
          icon: <ArchiveRestore size={13} />,
          onClick: handleArchiveActions,
          loading: unarchiveActionsMutation.isPending,
        }
      : {
          id: 'archive',
          type: 'button',
          label: t('capa.archive'),
          icon: <Archive size={13} />,
          onClick: handleArchiveActions,
          loading: archiveActionsMutation.isPending,
        },
  ];

  const planBulkActions: BulkActionDef[] = [
    isArchived
      ? {
          id: 'unarchive',
          type: 'button',
          label: t('capa.unarchive'),
          icon: <ArchiveRestore size={13} />,
          onClick: handleArchivePlans,
          loading: unarchivePlansMutation.isPending,
        }
      : {
          id: 'archive',
          type: 'button',
          label: t('capa.archive'),
          icon: <Archive size={13} />,
          onClick: handleArchivePlans,
          loading: archivePlansMutation.isPending,
        },
  ];

  const exportSelectedIds =
    view === 'actions' ? actionSelectedIds : planSelectedIds;

  // Plans/Actions section toggle — styled like the active/archive tabs and
  // rendered inside the table toolbar's top-left corner (leadingToolbar slot).
  const sectionToggle = (
    <ToggleGroup
      value={[view]}
      onValueChange={v => {
        const next = v[v.length - 1];
        if (next === 'plans' || next === 'actions') setView(next);
      }}
    >
      <ToggleGroupItem
        value='plans'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.plans')}
      </ToggleGroupItem>
      <ToggleGroupItem
        value='actions'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.actions')}
      </ToggleGroupItem>
    </ToggleGroup>
  );

  // Main toolbar CTAs — rendered inside the table toolbar and sized like other
  // tables: primary Create plan + secondary Export.
  const capaToolbarActions: ToolbarActionDef[] = [
    {
      id: 'create-plan',
      type: 'button',
      variant: 'default',
      label: t('capa.createPlan'),
      icon: <Plus />,
      onClick: () => setCreatePlanOpen(true),
    },
    {
      id: 'export',
      type: 'button',
      variant: 'outline',
      label: t('capa.export'),
      icon: <FileOutput />,
      onClick: () => setExportOpen(true),
    },
  ];

  // Active/Archived toggle under the main toolbar row — mirrors the
  // questionnaires table's active/archive tabs.
  const archiveToggle = (
    <ToggleGroup
      value={[isArchived ? 'archived' : 'active']}
      onValueChange={v => {
        const next = v[v.length - 1];
        if (!next) return;
        setIsArchived(next === 'archived');
        setActionSelection({});
        setPlanSelection({});
      }}
    >
      <ToggleGroupItem
        value='active'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.active')}
      </ToggleGroupItem>
      <ToggleGroupItem
        value='archived'
        className='text-xs px-3 hover:bg-brand-accent/10 aria-pressed:font-medium'
      >
        {t('capa.archived')}
      </ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    <div className='flex flex-col gap-4 p-4 @md:p-6'>
      {view === 'plans' ? (
        <AuditCapaPlansTable
          plans={plansData?.data ?? []}
          impactAreas={impactAreas ?? []}
          isLoading={plansLoading}
          t={t}
          tags={tags}
          onCreateTag={onCreateTag}
          canEdit={canEditPlan}
          onCellCommit={handlePlanCellCommit}
          rowSelection={planSelection}
          onRowSelectionChange={setPlanSelection}
          bulkActions={planBulkActions}
          leadingToolbar={sectionToggle}
          toolbarActions={capaToolbarActions}
          subTabs={archiveToggle}
        />
      ) : (
        <AuditCapaActionsTable
          actions={actionsData?.data ?? []}
          impactAreas={impactAreas ?? []}
          isLoading={actionsLoading}
          t={t}
          tags={tags}
          onCreateTag={onCreateTag}
          canEditField={canEditAction}
          onCellCommit={handleActionCellCommit}
          rowSelection={actionSelection}
          onRowSelectionChange={setActionSelection}
          bulkActions={actionBulkActions}
          leadingToolbar={sectionToggle}
          toolbarActions={capaToolbarActions}
          subTabs={archiveToggle}
        />
      )}

      <ExportFindingsDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        selectedIds={exportSelectedIds}
      />

      <CreatePlanDialog
        open={createPlanOpen}
        onOpenChange={setCreatePlanOpen}
        controller={createPlanController}
      />
    </div>
  );
}
